/**
 * Input formatters for sensitive identifier fields.
 *
 * The SSN, EIN and phone formatters are re-exported from `@order-up/common`,
 * whose one implementation every OrderUp form shares (Loi, 2026-10-28: "we have
 * global formatting utility make sure that is being used and no code is
 * replicated"). They:
 *
 *   1. Strip every non-digit character — pasting "123.45.6789" or
 *      "(123) 45 - 6789" still produces a clean result.
 *   2. Cap the underlying digit count at the maximum the identifier
 *      allows (9 for SSN/EIN, 10 for US phone). Extra characters are
 *      silently dropped.
 *   3. Insert the canonical formatting dashes / parentheses as the
 *      user types — the result is always a valid prefix of a
 *      correctly-formatted identifier.
 *
 * Usage pattern (in a React onChange handler):
 *
 *   onChange={(e) => handleChange('ownerSSN', formatSSN(e.target.value))}
 *
 * The component should ALSO set `maxLength={SSN_MAX_LENGTH}` (etc.)
 * and `inputMode="numeric"` to steer mobile keyboards and provide a
 * belt-and-suspenders defense against pathological input paths.
 */

export {
    EIN_MAX_LENGTH,
    SSN_MAX_LENGTH,
    formatEin as formatEIN,
    formatPhoneNumber as formatPhone,
    formatSsn as formatSSN,
} from '@order-up/common';

/** Max length of a formatted US phone string (includes parens, space, dash). */
export const PHONE_MAX_LENGTH = 14;

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Strip all non-digit characters. Useful for storage / validation. */
export function stripNonDigits(input: string): string {
    return input.replace(/\D/g, '');
}

/** Check if a formatted SSN is complete (9 digits). */
export function isCompleteSSN(input: string): boolean {
    return stripNonDigits(input).length === 9;
}

/** Check if a formatted EIN is complete (9 digits). */
export function isCompleteEIN(input: string): boolean {
    return stripNonDigits(input).length === 9;
}
